import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Search, NotebookPen, BarChart3, CalendarDays } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { secureRpc } from "@/lib/secureRpc";
import { useManagerNotes } from "@/hooks/useManagerNotes";
import NoteCard from "./NoteCard";
import NoteEditor from "./NoteEditor";

type Filter = "all" | "current" | "upcoming" | "past";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ManagerNotesPanel = ({ open, onOpenChange }: Props) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useUser();
  const { getToken } = useAuth();
  const { notes, loading, gameweeks, currentGw, saveNote, deleteNote, uploadImage } = useManagerNotes();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [team, setTeam] = useState<{ name: string | null; id: number | null }>({ name: null, id: null });
  const [favouriteBadge, setFavouriteBadge] = useState<string | null>(null);
  const [favouriteName, setFavouriteName] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !user) return;
    (async () => {
      try {
        const { data } = await secureRpc<any>("get_my_fpl_profile", {}, getToken);
        const row = Array.isArray(data) ? data[0] : data;
        setTeam({ name: row?.fpl_team_name ?? null, id: row?.fpl_team_id ?? null });
      } catch {
        /* non-blocking */
      }
      try {
        const { data } = await secureRpc<any>("get_my_profile_state", {}, getToken);
        const row = Array.isArray(data) ? data[0] : data;
        setFavouriteBadge(row?.badge_url ?? null);
        setFavouriteName(row?.favourite_team ?? row?.favorite_team ?? null);
      } catch {
        /* non-blocking */
      }
    })();
  }, [open, user, getToken]);

  const gwMap = useMemo(() => new Map(gameweeks.map((g) => [g.id, g])), [gameweeks]);

  const stateOf = (gwId: number): "past" | "current" | "future" =>
    gwId === currentGw ? "current" : gwId < currentGw ? "past" : "future";

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notes
      .filter((n) => {
        const s = stateOf(n.gameweek);
        if (filter === "current" && s !== "current") return false;
        if (filter === "upcoming" && s !== "future") return false;
        if (filter === "past" && s !== "past") return false;
        if (!q) return true;
        return (
          (n.title || "").toLowerCase().includes(q) ||
          n.body.toLowerCase().includes(q) ||
          (n.captain || "").toLowerCase().includes(q) ||
          `gw${n.gameweek}`.includes(q)
        );
      })
      .sort((a, b) => {
        const rank = (gw: number) => (gw === currentGw ? 0 : gw > currentGw ? 1 : 2);
        const r = rank(a.gameweek) - rank(b.gameweek);
        if (r !== 0) return r;
        return rank(a.gameweek) === 2 ? b.gameweek - a.gameweek : a.gameweek - b.gameweek;
      });
  }, [notes, search, filter, currentGw]);

  const editingNote = editing && editing !== "new" ? notes.find((n) => n.id === editing) : null;
  const takenGameweeks = notes.map((n) => n.gameweek);

  const goto = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <SheetHeader className="px-4 pt-4 pb-3 border-b border-border/60">
            <SheetTitle className="flex items-center gap-2 text-base">
              <NotebookPen className="w-4 h-4 text-primary" />
              Manager Notes
            </SheetTitle>
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              {favouriteBadge && (
                <img src={favouriteBadge} alt={favouriteName || "Favourite team"} className="w-5 h-5 object-contain" />
              )}
              <Badge variant="outline" className="text-[10px]">
                {team.name || "FPL team not linked"}
              </Badge>
              <Badge variant="secondary" className="text-[10px]">GW{currentGw}</Badge>
              <button
                onClick={() => goto("/fpl-hub?tab=performance")}
                className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline"
              >
                <BarChart3 className="w-3 h-3" /> Performance
              </button>
              <button
                onClick={() => goto("/fpl-hub?tab=deadline")}
                className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline"
              >
                <CalendarDays className="w-3 h-3" /> Deadline
              </button>
            </div>
          </SheetHeader>

          <div className="px-4 py-3 space-y-2 border-b border-border/60">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search notes"
                className="h-9 pl-8 text-xs"
              />
            </div>
            <div className="flex items-center gap-1.5 overflow-x-auto">
              {(["all", "current", "upcoming", "past"] as Filter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-2.5 py-1 rounded-full text-[11px] border whitespace-nowrap ${
                    filter === f
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border/60 text-muted-foreground"
                  }`}
                >
                  {f === "all" ? "All" : f === "current" ? "Current GW" : f === "upcoming" ? "Upcoming" : "Past"}
                </button>
              ))}
              <Button
                size="sm"
                className="h-7 text-[11px] ml-auto shrink-0"
                onClick={() => setEditing("new")}
                disabled={editing === "new"}
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> New
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
            {editing && (
              <div className="rounded-lg border border-primary/40 bg-card p-3">
                <NoteEditor
                  key={editing}

                  gameweeks={gameweeks}
                  defaultGameweek={currentGw}
                  existing={editingNote}
                  takenGameweeks={takenGameweeks}
                  onCancel={() => setEditing(null)}
                  onSave={saveNote}
                  onUploadImage={uploadImage}
                />
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              </div>
            ) : visible.length === 0 && !editing ? (
              <div className="text-center py-10 space-y-2">
                <NotebookPen className="w-8 h-8 mx-auto text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  No notes yet. Plan your gameweek — captain picks, transfers and chips.
                </p>
                <Button size="sm" className="text-xs" onClick={() => setEditing("new")}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Create your first note
                </Button>
              </div>
            ) : (
              visible.map((n) => (
                <NoteCard
                  key={n.id}
                  note={n}
                  gw={gwMap.get(n.gameweek)}
                  state={stateOf(n.gameweek)}
                  onEdit={() => setEditing(n.id)}
                  onDelete={() => setPendingDelete(n.id)}
                  onOpenPerformance={() => goto("/fpl-hub?tab=performance")}
                />
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const id = pendingDelete!;
                setPendingDelete(null);
                try {
                  await deleteNote(id);
                  toast({ title: "Note deleted" });
                } catch (e: any) {
                  toast({ title: "Could not delete", description: e.message, variant: "destructive" });
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ManagerNotesPanel;
